import _ from 'lodash';
import { NumberService } from 'Services';

const CURRENCY_KEY = 'currency';

function replaceAll(str: string, search: string, replacement: string) {
    let newStr = '';
    if (_.isString(str)) {
        newStr = str.split(search).join(replacement);
    }
    return newStr;
}

/**
 * return array of words that are within square brackes:
 * More than [threshold] IP addresses for one card
 * returns ['[threshold]']
 */
const listOfSquareBracketParameters = (str: string) => str.match(/\[(.*?)\]/g);

// returns ['threshold', 'dog'] from ['[threshold]', '[dog]']
const extractWordFromSquareBrackets = (str: string) => {
    if (str[0] !== '[' && str[str.length] !== ']') {
        return str;
    }

    return str.substring(1, str.length - 1);
};

const replaceParamsWithEmptyString = (squareParams: string[], str: string) =>
    _.reduce(squareParams, (acc, curr) => replaceAll(acc, curr, ''), str);

// abstract out the getting of the value so that we can apply any kind of
// transforamtions to the value as we see fit
const getReplacementValue = (ctx: { [key: string]: any }, key: string) => {
    const val = _.get(ctx, key);
    switch (key) {
        case CURRENCY_KEY: {
            const possibleReturn = NumberService.getLocaleCurrency(val);
            return _.isNaN(possibleReturn) ? val : possibleReturn;
        }
        default:
            return val;
    }
};

const replaceParamsWithParamMap = (squareParams: string[], str: string, ctx: { [key: string]: any }): string => {
    const retString = _.reduce(
        squareParams,
        (acc, param) => {
            const key = extractWordFromSquareBrackets(param);
            const replacementValue = getReplacementValue(ctx, key);
            // if we don't have the replacemenet value, don't bother trying to update the string.
            if (replacementValue === undefined) return acc;

            const formattedReplacementValue =
                typeof replacementValue === 'number'
                    ? NumberService.getLocaleNumber(replacementValue)
                    : replacementValue;
            return replaceAll(acc, param, formattedReplacementValue);
        },
        str
    );

    return retString;
};

type Options = {
    parameterMap?: { [key: string]: any };
    type: 'emptyString' | 'parameterMap';
};

/**
 *
 * @param str a string like "Accept alll [value] under [threshold]"
 * @param options options to change replacemenet behavior of this function.
 * parameterMap should have keys that map to the values inside of square brackets
 * @returns the tranformed string or the input string if a transformation was not possible
 */
export const strParamReplacer = (str: string | undefined, options: Options) => {
    /** early return */
    if (!str) return undefined;
    const squareParams = listOfSquareBracketParameters(str);
    if (!squareParams) return str;
    /** end early return */

    switch (options.type) {
        case 'emptyString': {
            return replaceParamsWithEmptyString(squareParams, str);
        }
        case 'parameterMap': {
            return replaceParamsWithParamMap(squareParams, str, options.parameterMap || {});
        }
        default: {
            return str;
        }
    }
};

export const getEmptyStringReplacementText = (str: string | undefined) => {
    if (!str) {
        return '--';
    }
    return str;
};

export const snakeCaseToTitleCase = (str: string) => {
    return str
        .replace(/_/g, ' ')
        .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.substring(1).toLowerCase());
};
